import useMount from 'react-use/esm/useMount';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
import { SOFTWARE_RENDERER_PATTERNS } from 'common/SystemCheck/hardwareAcceleration';
// example values provided for Macbook M2
interface SystemInfo {
  webgpu: {
    // ex 'metal-3', 'vulkan-1'
    architecture: string;
    // ex 'apple', 'nvidia', 'intel'
    vendor: string;
    supported: boolean;
    // VRAM proxy signals — analytics-only, not used for gating.
    // ex. 268435456
    maxBufferSize: number | null;
    // ex. 134217728
    maxStorageBufferBindingSize: number | null;
  };
  webgl: {
    // ex. ANGLE (Apple, ANGLE Metal Renderer: Apple M1 Max, Unspecified Version)
    renderer: string;
    // ex. Google Inc. (Apple)
    vendor: string;
    // ex. WebGL 1.0 (OpenGL ES 2.0 Chromium)
    version: string;
    experimental: boolean;
    // VRAM proxy signals — analytics-only, not used for gating.
    // ex. 16384
    maxTextureSize: number | null;
    // Heuristic from the renderer string: integrated GPUs share system RAM
    // and tend to underperform on heavy 3D pixel data.
    isIntegrated: boolean;
    // Parsed from the renderer string so analytics can group by graphics
    // backend; the raw string embeds the GPU model and is too
    // high-cardinality to facet on.
    angleBackend: AngleBackend;
    // True when the renderer is a known software rasterizer (SwiftShader,
    // llvmpipe, Microsoft Basic Render Driver): hardware acceleration is off
    // or GPU init failed, and the viewer will be slow. Note SwiftShader
    // reports a Vulkan ANGLE backend, so angleBackend alone cannot show this.
    isSoftware: boolean;
  };
  system: {
    // ex. 10
    cpuCores: string | number;
  };
}

interface GPUSupportedLimits {
  maxBufferSize?: number;
  maxStorageBufferBindingSize?: number;
}

interface GPUAdapterInfo {
  vendor?: string;
  architecture?: string;
}

interface GPUAdapter {
  info?: GPUAdapterInfo;
  limits?: GPUSupportedLimits;
}

interface GPU {
  requestAdapter(): Promise<GPUAdapter | null>;
}

interface NavigatorGPU extends Navigator {
  gpu?: GPU;
}

const INTEGRATED_RENDERER_PATTERNS: ReadonlyArray<RegExp> = [
  /\bIntel\b.*?\b(?:UHD|HD|Iris)\b/i,
  /\bAMD\b.*?\bRadeon (?:R[1-9]|Vega) Graphics\b/i,
  /\bApple M\d/i,
];

const isIntegratedRenderer = (renderer: string): boolean =>
  INTEGRATED_RENDERER_PATTERNS.some((p) => p.test(renderer));

// Reuses the SystemCheck pattern list so telemetry and the user-facing
// hardware acceleration check can never disagree on what counts as software.
export const isSoftwareRenderer = (renderer: string): boolean =>
  typeof renderer === 'string' &&
  SOFTWARE_RENDERER_PATTERNS.some(({ pattern }) => pattern.test(renderer));

// 'none' means a real renderer string that is not ANGLE (e.g. Firefox, Safari).
// 'unavailable' means WebGL info could not be read (blocked or broken context,
// masked debug info). That is the signature of a GPU-init failure, kept separate
// from healthy non-ANGLE browsers so it stays alertable on its own.
export type AngleBackend =
  | 'metal'
  | 'd3d11'
  | 'd3d9'
  | 'vulkan'
  | 'opengl'
  | 'none'
  | 'unavailable'
  | 'unknown';

const ANGLE_BACKEND_PATTERNS: ReadonlyArray<[RegExp, AngleBackend]> = [
  // The GL backend on macOS mentions Metal too ("OpenGL 4.1 Metal - 88",
  // Apple's GL driver runs on Metal), so the Metal match must be the
  // backend-specific "ANGLE Metal" form and OpenGL must come after it.
  [/ANGLE Metal/i, 'metal'],
  [/Direct3D11|\bD3D11\b/i, 'd3d11'],
  [/Direct3D9|\bD3D9\b/i, 'd3d9'],
  [/\bVulkan\b/i, 'vulkan'],
  [/\bOpenGL\b/i, 'opengl'],
];

export const getAngleBackend = (renderer: string): AngleBackend => {
  // getParameter() is typed any and can return null on privacy-hardened or
  // broken contexts; getWebGLInfo also passes its 'Unavailable' sentinel here.
  if (typeof renderer !== 'string' || renderer === '' || renderer === 'Unavailable') {
    return 'unavailable';
  }
  if (!renderer.includes('ANGLE')) return 'none';
  return ANGLE_BACKEND_PATTERNS.find(([pattern]) => pattern.test(renderer))?.[1] ?? 'unknown';
};

const getCpuInfo = () => {
  const cpuCores = navigator.hardwareConcurrency ?? 'Unavailable';
  return { cpuCores };
};

const getWebGPUInfo = async () => {
  let supported = false;
  let vendor = 'Unavailable';
  let architecture = 'Unavailable';
  let maxBufferSize: number | null = null;
  let maxStorageBufferBindingSize: number | null = null;

  const castedGPUNavigator = navigator as NavigatorGPU;

  if ('gpu' in castedGPUNavigator) {
    try {
      const adapter = await castedGPUNavigator.gpu?.requestAdapter();
      if (adapter) {
        const info = adapter.info ?? null;
        supported = true;
        vendor = info?.vendor ?? 'Unavailable';
        architecture = info?.architecture ?? 'Unavailable';
        maxBufferSize = adapter.limits?.maxBufferSize ?? null;
        maxStorageBufferBindingSize = adapter.limits?.maxStorageBufferBindingSize ?? null;
      }
    } catch (err) {
      console.error('Error requesting GPU adapter:', err);
    }
  }

  return {
    architecture,
    vendor,
    supported,
    maxBufferSize,
    maxStorageBufferBindingSize,
  };
};

const getWebGLInfo = () => {
  let renderer = 'Unavailable';
  let vendor = 'Unavailable';
  let version = 'Unavailable';
  let experimental = false;
  let maxTextureSize: number | null = null;

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    const experimentalgl = canvas.getContext('experimental-webgl');

    if (gl == null && experimentalgl != null) {
      experimental = true;
    }

    const useableContext = gl ?? experimentalgl;

    if (useableContext && useableContext instanceof WebGLRenderingContext) {
      const debugInfo = useableContext.getExtension('WEBGL_debug_renderer_info');
      version = useableContext.getParameter(useableContext.VERSION);
      renderer = debugInfo
        ? useableContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : 'Unavailable';
      vendor = debugInfo
        ? useableContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : 'Unavailable';
      maxTextureSize = useableContext.getParameter(useableContext.MAX_TEXTURE_SIZE) ?? null;
      // clean up canvas
      useableContext?.getExtension('WEBGL_lose_context')?.loseContext();
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    }
  } catch (err) {
    console.error('Error collecting WebGL info:', err);
  }

  return {
    renderer,
    vendor,
    version,
    experimental,
    maxTextureSize,
    isIntegrated: typeof renderer === 'string' && isIntegratedRenderer(renderer),
    angleBackend: getAngleBackend(renderer),
    isSoftware: isSoftwareRenderer(renderer),
  };
};

export async function collectSystemInfo(): Promise<SystemInfo> {
  const result: SystemInfo = {
    system: getCpuInfo(),
    webgpu: await getWebGPUInfo(),
    webgl: getWebGLInfo(),
  };

  return result;
}

const logSystemInfo = async () => {
  const systemInfo = await collectSystemInfo();
  analytics.addContext(globalContext.viewer.systemInfo, systemInfo);
};

export const useSystemInfoAnalytics = () => {
  useMount(() => {
    logSystemInfo();
  });
};
